import { useState } from 'react';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import type { FulfillmentOrderLine } from '@/infrastructure/FulfillmentAdminClient';
import type { ReturnLabel } from '@/models/FulfillmentPrint';
import { PrintIcon } from '@/pages/fulfillment/components/UltimateIcons';
import { DELIVERY_COMPANIES } from '@/pages/oms/orders/utils/deliveryCompany';

/** The carriers the warehouse books supplier returns with, named the way the OMS names them. */
const CARRIER_CODES = ['K003', 'K004', 'K006', 'K001', 'K011'];
const CARRIERS = CARRIER_CODES.flatMap(
  (code) => DELIVERY_COMPANIES.find((company) => company.code === code)?.name ?? [],
);

interface LabelRow {
  lineNo: number;
  name: string;
  size: string;
  qty: string;
}

const initialRows = (lines: FulfillmentOrderLine[]): LabelRow[] =>
  lines.map((line) => ({
    lineNo: line.lineNo,
    name: [line.brand, line.productNameKr ?? line.productName].filter(Boolean).join(' ') || line.sku,
    size: line.size ?? '',
    qty: String(line.totalQuantity),
  }));

/** Size and quantity are optional on the printed label — an empty one is left off rather than blank. */
const labelLine = (row: LabelRow): string =>
  [row.name, row.size, row.qty ? `${row.qty}개` : ''].filter(Boolean).join(' · ');

export interface ReturnLabelModalProps {
  lines: FulfillmentOrderLine[];
  /** The page owns the printer and the toast; this sheet hands over the label exactly as it previews it. */
  onPrint: (lineNos: number[], label: ReturnLabel) => void;
  onClose: () => void;
}

/** Nothing is sent anywhere — the label is printed and taped to the box, which is why the carrier is picked. */
export function ReturnLabelModal({ lines, onPrint, onClose }: ReturnLabelModalProps) {
  const [carrier, setCarrier] = useState(CARRIERS[0] ?? '');
  const [custom, setCustom] = useState('');
  const [rows, setRows] = useState<LabelRow[]>(() => initialRows(lines));

  const printed = custom.trim() || carrier;

  const edit = (lineNo: number, patch: Partial<LabelRow>) =>
    setRows((previous) => previous.map((row) => (row.lineNo === lineNo ? { ...row, ...patch } : row)));

  const dialogRef = useModalDialog(onClose);

  return (
    <dialog className="vo-modal wide" ref={dialogRef} aria-label="Print Return Labels">
      <header>
        Print Return Labels — {lines.length} Selected Product(s) (Supplier Return)
        <button type="button" className="x" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body">
        <b>Carrier</b>
        <div className="carrier-chips">
          {CARRIERS.map((name) => (
            <button
              key={name}
              type="button"
              className={`cchip${!custom.trim() && carrier === name ? ' on' : ''}`}
              onClick={() => {
                setCarrier(name);
                setCustom('');
              }}
            >
              {name}
            </button>
          ))}
        </div>
        <input
          className="carrier-custom"
          aria-label="Custom carrier"
          placeholder="✎ Enter carrier name"
          value={custom}
          onChange={(event) => setCustom(event.target.value)}
        />

        <table className="tbl">
          <thead>
            <tr>
              <th>Product Name KR</th>
              <th>Size (optional)</th>
              <th>Qty (optional)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.lineNo}>
                <td>{row.name}</td>
                <td>
                  <input
                    aria-label={`Size for ${row.name}`}
                    value={row.size}
                    placeholder="omitted if empty"
                    onChange={(event) => edit(row.lineNo, { size: event.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={`Qty for ${row.name}`}
                    value={row.qty}
                    onChange={(event) => edit(row.lineNo, { qty: event.target.value.replace(/\D/g, '') })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <b>Label Preview</b>
        <div className="label-preview">
          <div className="carrier">{printed}</div>
          {rows.map((row) => (
            <div key={row.lineNo}>{labelLine(row)}</div>
          ))}
        </div>
        <div className="note">
          Carrier name · product name (KR) · size · qty go on the label; empty size or qty is left off. Tape it to the
          box going back to the supplier.
        </div>
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-blue btn-sm"
          disabled={!printed}
          // The preview's own lines are what gets printed, so the sticker cannot drift from what the desk checked.
          onClick={() =>
            onPrint(
              rows.map((row) => row.lineNo),
              { carrier: printed, lines: rows.map(labelLine) },
            )
          }
        >
          <PrintIcon />
          Print
        </button>
      </div>
    </dialog>
  );
}
